import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpAZ, ArrowDownAZ, FilterX, Filter, ChevronRight } from 'lucide-react';
import {
  FILTER_OPS, SORT_LABELS, TYPE_MENU_LABEL, normalizeConditionFilter, describeConditionFilter,
} from '../../../utils/columnFilterTypes';
import DateValueTree from './DateValueTree';
import CustomFilterDialog from './CustomFilterDialog';

// Checkbox rows drawn at most — a 26K-value column froze the menu when every value was drawn.
// Search, auto-tick and Select All still cover every value, not just the drawn ones.
export const OPTION_RENDER_CAP = 300;

const FLYOUT_WIDTH = 220;
// Every menu opens at this width; only its own row labels (a long "Clear Filter From" column name)
// can widen it, and that is measured before the first paint so it never grows after opening. Values
// never widen it — they arrive after opening on server-paged grids, so they truncate instead.
export const MENU_WIDTH = 260;
// Value list height in rows: (Select All) + six values.
export const LIST_ROWS = 7;
const VIEWPORT_GAP = 8;
const FALLBACK_ROW_HEIGHT = 26;
// Preview mode: in the page flow rather than fixed to a header.
const INLINE_STYLE = { position: 'relative', zIndex: 'auto', maxHeight: 'none' };

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** Menu width: the standard width, wider only when a row label needs it, never past the viewport. */
export const menuWidth = (neededPx, viewportWidth) =>
  Math.min(Math.max(MENU_WIDTH, Math.ceil(neededPx || 0)), viewportWidth - 2 * VIEWPORT_GAP);

/**
 * Where the menu goes for its header (anchor = the header's rect) at its real size: below the
 * header; above it when below would run off the screen and above fits; otherwise pinned to the
 * bottom edge. Left follows the header, pulled in from the right edge.
 */
export const placeMenu = (anchor, size, viewport) => {
  const left = clamp(anchor.left, VIEWPORT_GAP, viewport.width - VIEWPORT_GAP - size.width);
  const below = anchor.bottom + 4;
  if (below + size.height <= viewport.height - VIEWPORT_GAP) return { top: below, left };
  const above = anchor.top - 4 - size.height;
  if (above >= VIEWPORT_GAP) return { top: above, left };
  return { top: Math.max(VIEWPORT_GAP, viewport.height - VIEWPORT_GAP - size.height), left };
};

/**
 * STUI Column Filter — the grid column header menu, on Excel's layout. One skeleton; the column's
 * type switches sections on:
 *   Sort (labels per type) · Clear Filter From "<col>" · "<Type> Filters ▸" operator flyout ->
 *   Custom Filter dialog · Search (auto-tick) + value list (flat, or a Year > Month > Day tree for
 *   dates) · OK / Cancel.
 * type 'none' = sort only; textOnly = conditions without a value list (long text).
 *
 * The menu owns only its own UI state. The column's filter is either the ticked values (string[])
 * or a condition filter (utils/columnFilterTypes); onApply hands back the new one ([] clears).
 *
 * Props: column {key,label}, type 'text'|'number'|'date'|'none', textOnly, filter, options
 * (every value for this column, already cascaded), optionsLoading, optionsTruncated, sortConfig,
 * onSort(direction), onApply(filter), onClose(), anchor (the header's DOMRect — the menu places
 * itself from its own measured size); inline renders it in the page flow (STUI page previews)
 * instead, and outside clicks do not close it.
 *
 * Size: MENU_WIDTH (or wider for a long row label, decided before paint), a value list LIST_ROWS
 * tall, capped to the viewport. The corner grip resizes it for this opening only.
 */
const ColumnFilterMenu = ({
  column, type = 'text', textOnly = false, filter, options = [], optionsLoading = false,
  optionsTruncated = false, sortConfig, onSort, onApply, onClose, anchor, inline = false,
}) => {
  const menuRef = useRef(null);
  const baseSize = useRef(null);
  const conditionActive = !!normalizeConditionFilter(filter);
  const listActive = Array.isArray(filter) && filter.length > 0;

  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(() => (Array.isArray(filter) ? filter : []));
  const [touched, setTouched] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // null | { presetOp }
  const [flipLeft, setFlipLeft] = useState(false);
  const [measured, setMeasured] = useState(null); // { width, listHeight }, fixed before the first paint
  const [dragSize, setDragSize] = useState(null);  // { width, height } from the corner grip

  // Size once, before paint: the widest row label (menu items don't wrap) and the real row height.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rows = [...el.querySelectorAll('.stui-filter-menu-item')];
    const needed = Math.max(0, ...rows.map(r => r.scrollWidth)) + (el.offsetWidth - el.clientWidth);
    const rowHeight = el.querySelector('.stui-filter-option-select-all')?.offsetHeight || FALLBACK_ROW_HEIGHT;
    setMeasured({ width: menuWidth(needed, window.innerWidth), listHeight: rowHeight * LIST_ROWS + 2 });
  }, []);

  // Place from the measured size — still before paint. A grip drag keeps the top-left corner put.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (inline || !el || !anchor || !measured) return;
    const rect = el.getBoundingClientRect();
    const { top, left } = placeMenu(anchor, rect, { width: window.innerWidth, height: window.innerHeight });
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    setFlipLeft(left + rect.width + FLYOUT_WIDTH > window.innerWidth);
  }, [anchor, inline, measured]);

  // Corner grip: drag to resize, from the opening size up to the viewport edge. Not remembered.
  // A menu with no room to its right (flipLeft — the flyout's rule) has the grip bottom-left and
  // grows leftward: the right edge and top stay put while the left edge follows the pointer.
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    if (!baseSize.current) baseSize.current = { width: rect.width, height: rect.height };
    const min = baseSize.current;
    const start = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
    const leftward = flipLeft;
    const onMove = ev => {
      const width = leftward
        ? clamp(start.width + start.x - ev.clientX, min.width, rect.right - VIEWPORT_GAP)
        : clamp(start.width + ev.clientX - start.x, min.width, window.innerWidth - rect.left - VIEWPORT_GAP);
      if (leftward) el.style.left = `${rect.right - width}px`;
      setDragSize({
        width,
        height: clamp(start.height + ev.clientY - start.y, min.height, window.innerHeight - rect.top - VIEWPORT_GAP),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Click outside closes — the Custom Filter dialog renders inside this element, so it counts as inside.
  useEffect(() => {
    if (inline) return undefined;
    const onDown = e => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose, inline]);

  const needle = search.trim().toLowerCase();
  const visible = useMemo(
    () => (needle ? options.filter(o => o.toLowerCase().includes(needle)) : options),
    [options, needle]
  );
  const selected = useMemo(() => new Set(pending), [pending]);
  const allVisibleSelected = visible.length > 0 && visible.every(v => selected.has(v));

  const setTicks = (next) => { setPending(next); setTouched(true); };

  // Excel auto-tick: the ticks follow the search — exactly the matching values; clearing the search
  // restores the column's current value selection.
  const onSearch = (q) => {
    setSearch(q);
    const n = q.trim().toLowerCase();
    if (n) setTicks(options.filter(o => o.toLowerCase().includes(n)));
    else { setPending(Array.isArray(filter) ? filter : []); setTouched(false); }
  };

  const toggleValues = (values, checked) => {
    const next = new Set(pending);
    values.forEach(v => (checked ? next.add(v) : next.delete(v)));
    setTicks([...next]);
  };
  const toggleAllVisible = () => toggleValues(visible, !allVisibleSelected);

  const applyList = () => {
    if (!touched && conditionActive) { onClose?.(); return; } // untouched list keeps the condition
    const all = new Set(pending);
    const everything = !optionsTruncated && options.length > 0 && options.every(o => all.has(o));
    onApply?.(everything ? [] : pending);
  };

  const sortLabels = SORT_LABELS[type] || SORT_LABELS.text;
  const sortActive = (dir) => sortConfig?.key === column?.key && sortConfig?.direction === dir;
  const label = column?.label || column?.key;
  const showList = type !== 'none' && !textOnly;

  // Dynamic sizes only; everything static is in STUI. A dragged menu hands its height to the list.
  const menuStyle = {
    ...(inline ? INLINE_STYLE : { maxHeight: window.innerHeight - 2 * VIEWPORT_GAP }),
    width: dragSize?.width ?? measured?.width ?? MENU_WIDTH,
    ...(dragSize ? { height: dragSize.height } : null),
  };
  const listStyle = dragSize
    ? { flex: '1 1 0', height: 'auto', maxHeight: 'none', minHeight: 0 }
    : measured ? { flex: '0 1 auto', height: measured.listHeight, maxHeight: 'none', minHeight: 0 } : undefined;

  if (dialog) {
    return (
      // The menu box hides behind the dialog but stays mounted, so the dialog counts as "inside"
      // for click-outside.
      <div ref={menuRef} className="stui-filter-dropdown dialog-open" onClick={e => e.stopPropagation()}>
        <CustomFilterDialog
          column={column}
          type={type}
          filter={filter}
          presetOp={dialog.presetOp}
          onApply={f => { setDialog(null); onApply?.(f); }}
          onCancel={() => { setDialog(null); onClose?.(); }}
        />
      </div>
    );
  }

  return (
    <div ref={menuRef} className="stui-filter-dropdown" style={menuStyle} onClick={e => e.stopPropagation()}>
      <div className="stui-filter-sort-section">
        <button type="button" className={`stui-filter-sort-btn ${sortActive('asc') ? 'active' : ''}`} onClick={() => onSort?.('asc')}>
          <ArrowUpAZ size={16} /><span>{sortLabels.asc}</span>
        </button>
        <button type="button" className={`stui-filter-sort-btn ${sortActive('desc') ? 'active' : ''}`} onClick={() => onSort?.('desc')}>
          <ArrowDownAZ size={16} /><span>{sortLabels.desc}</span>
        </button>
      </div>

      {type !== 'none' && (<>
        <div className="stui-filter-divider" />
        <button
          type="button"
          className={`stui-filter-menu-item${conditionActive || listActive ? '' : ' disabled'}`}
          disabled={!(conditionActive || listActive)}
          onClick={() => onApply?.([])}
        >
          <FilterX size={16} /><span>Clear Filter From "{label}"</span>
        </button>
        <div
          className="stui-filter-menu-item"
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={flyoutOpen}
          onMouseEnter={() => setFlyoutOpen(true)}
          onMouseLeave={() => setFlyoutOpen(false)}
          onClick={() => setFlyoutOpen(o => !o)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlyoutOpen(o => !o); } }}
        >
          <Filter size={16} /><span>{TYPE_MENU_LABEL[type]}</span>
          <ChevronRight size={14} className="stui-filter-menu-chevron" />
          {flyoutOpen && (
            <div className={`stui-filter-flyout${flipLeft ? ' flip-left' : ''}`} role="menu">
              {FILTER_OPS[type].map(o => (
                <React.Fragment key={o.value}>
                  {o.sep && <div className="stui-filter-divider" />}
                  <button type="button" role="menuitem" className="stui-filter-menu-item"
                    onClick={e => { e.stopPropagation(); setDialog({ presetOp: o.value }); }}>
                    <span>{o.label}…</span>
                  </button>
                </React.Fragment>
              ))}
              <div className="stui-filter-divider" />
              <button type="button" role="menuitem" className="stui-filter-menu-item"
                onClick={e => { e.stopPropagation(); setDialog({ presetOp: null }); }}>
                <span>Custom Filter…</span>
              </button>
            </div>
          )}
        </div>
      </>)}

      {showList && (<>
        <div className="stui-filter-divider" />
        <div className="stui-filter-search">
          <input type="text" placeholder="Search" value={search} onChange={e => onSearch(e.target.value)} />
        </div>
        <div className="stui-filter-options" style={listStyle}>
          {conditionActive && !touched && (
            <div className="stui-filter-option">Filtered: {describeConditionFilter(filter)}</div>
          )}
          <label className="stui-filter-option stui-filter-option-select-all">
            <input type="checkbox" className="stui-checkbox-input" checked={allVisibleSelected} onChange={toggleAllVisible} />
            <span>{needle ? '(Select All Search Results)' : '(Select All)'}</span>
          </label>
          {optionsLoading && <div className="stui-filter-option">Loading…</div>}
          {type === 'date' ? (
            <DateValueTree values={visible} selected={selected} onToggle={toggleValues} searching={!!needle} />
          ) : (
            visible.slice(0, OPTION_RENDER_CAP).map(option => (
              <label key={option} className="stui-filter-option">
                <input type="checkbox" className="stui-checkbox-input" checked={selected.has(option)} onChange={() => toggleValues([option], !selected.has(option))} />
                <span className="stui-filter-option-label" title={option || '(Blanks)'}>{option || '(Blanks)'}</span>
              </label>
            ))
          )}
          {type !== 'date' && visible.length > OPTION_RENDER_CAP && (
            <div className="stui-filter-option">Showing {OPTION_RENDER_CAP} of {visible.length.toLocaleString()} — type to narrow</div>
          )}
          {optionsTruncated && (
            <div className="stui-filter-option">First {options.length.toLocaleString()} values — type to narrow</div>
          )}
        </div>
        <div className="stui-filter-footer">
          <button type="button" onClick={applyList} className="stui-filter-ok-btn">OK</button>
          <button type="button" onClick={() => onClose?.()} className="stui-filter-cancel-btn">Cancel</button>
        </div>
        <div className={`stui-filter-resize-grip${flipLeft ? ' flip-left' : ''}`} role="presentation" title="Drag to resize" onPointerDown={startResize} />
      </>)}
    </div>
  );
};

export default ColumnFilterMenu;
